import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { ConfirmDialog } from 'primereact/confirmdialog';

import { UserFormSearchType, userSearchFormSchema } from '@/shared/schema/user';
import { auth, db } from '@/lib/firebase';
import { ConversationType, UserType } from '@/types/chat';
import { ChatBox, ConfirmJoinGroup, ConfirmOutGroup } from '../chatbox';
import {
  ChatAside,
  UserItemSkeleton,
  ConversationItem,
  SearchList,
} from '../aside';
import { INVITE_STATUS } from '@/shared/constants/chat';
import { useChat } from '@/components/chat/ChatContext';
import { ObjectLiteral } from '@/types/object';
import { apiRequest } from '@/services';
import { PaginationInfo } from '@/types/pagination';
import useDebounceFn from '@/hooks/useDebounceFn';
import useFirebaseAuthUser from '@/hooks/useFirebaseAuthUser';
import PATH from '@/routes/path';
import { STATUS } from '@/shared/constants/user';
import { signOut } from '@firebase/auth';
import { DEFAULT_PER_PAGE } from '@/shared/constants/pagination';

const DEFAULT_PAGINATION = {
  current_page: 0,
  per_page: 10,
  total_page: 1,
  total_item: 1,
  has_more: true,
};

let controllerVersion = new AbortController();

const createUserLookup = (users: UserType[]): { [key: number]: UserType } => {
  return users.reduce((lookup: { [key: number]: UserType }, user: UserType) => {
    lookup[user.id] = user;
    return lookup;
  }, {});
};

const mapConversationsWithMembers = (
  conversations: ConversationType[],
  userLookup: { [key: number]: UserType },
): ConversationType[] => {
  return conversations.map((chat) => ({
    ...chat,
    members: chat.member_ids ? chat.member_ids.map((id) => userLookup[id]) : [],
  }));
};

const filterConversations = (
  conversations: ConversationType[],
  keyword: string,
): ConversationType[] => {
  return conversations.filter((item) =>
    item.name?.toLowerCase()?.includes(keyword.toLowerCase()),
  );
};

const Conversation: React.FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { apiService } = apiRequest();
  const [isFetching, setIsFetching] = useState(false);
  const [contacts, setContacts] = useState<UserType[]>([]);
  const [pagination, setPagination] =
    useState<PaginationInfo>(DEFAULT_PAGINATION);
  const [selectedConversation, setSelectedConversation] = useState<
    ConversationType | null | undefined
  >(null);
  const [conversationsFilted, setConversationFiltered] = useState<
    ConversationType[]
  >([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [openPopup, setOpenPopup] = useState(false);
  const [openPopupRemove, setOpenPopupRemove] = useState(false);
  const [prevMemberIds, setPrevMemberIds] = useState<number[]>([]);
  const listRef = useRef<HTMLDivElement>(null);
  const listContactRef = useRef<HTMLDivElement>(null);
  const { currentUser, ready: isAuthReady } = useFirebaseAuthUser();
  const { conversationId: id } = useParams<{
    conversationId: string | undefined;
  }>();

  const {
    conversations,
    isNotSeenMap,
    isNeedConfirmMap,
    loading,
    messageStatus,
  } = useChat();

  const isAccepted =
    (selectedConversation &&
      selectedConversation?.invitation_statuses?.[Number(currentUser?.uid)]
        ?.status !== INVITE_STATUS.PENDING) ||
    !selectedConversation?.is_group_chat;
  const isRemoved =
    selectedConversation &&
    selectedConversation.is_group_chat &&
    selectedConversation.invitation_statuses?.[Number(currentUser?.uid)]
      ?.status === INVITE_STATUS.REMOVED;

  const formMethods = useForm<UserFormSearchType>({
    mode: 'onChange',
    resolver: yupResolver(userSearchFormSchema),
    shouldFocusError: false,
    defaultValues: {
      keyword: '',
    },
  });

  const { handleSubmit } = formMethods;

  const handleGetConversationFilter = (keyword: string) => {
    const userLookup = createUserLookup(users);
    const conversationsWithMembers = mapConversationsWithMembers(
      conversations,
      userLookup,
    );
    const formatKeyWord = keyword?.trim()?.toLowerCase();
    const filteredConversations = filterConversations(
      conversationsWithMembers,
      formatKeyWord,
    );

    setConversationFiltered(keyword ? filteredConversations : conversations);
  };

  const handleFetchContacts = useCallback(
    async (query?: ObjectLiteral) => {
      setIsFetching(true);

      if (controllerVersion.abort) controllerVersion.abort();
      controllerVersion = new AbortController();
      const { signal } = controllerVersion;

      const { data: res, error } = await apiService.contacts.query(
        '',
        {
          paginate: (pagination?.current_page || 0) + 1,
          ...query,
          limit: DEFAULT_PAGINATION.per_page,
        },
        { signal },
      );

      if (res) {
        setContacts((prevContacts) => [...prevContacts, ...res.data]);
        setPagination(res.pagination as PaginationInfo);
        setIsFetching(false);
      }

      if (error) {
        if (signal?.aborted && error === 'error') return;
        setContacts([]);
        setIsFetching(false);
      }
    },
    [pagination, isFetching],
  );

  const debouncedSearch = useDebounceFn((keyword: string) => {
    const params = {
      keyword: keyword,
      paginate: 1,
    };
    handleFetchContacts(params);
    setContacts([]);
  }, 300);

  const onSubmit = (value: UserFormSearchType) => {
    handleGetConversationFilter(String(value.keyword));
    debouncedSearch(value.keyword);
  };

  const fetchUsers = async (ids: number[]) => {
    // Corrupted data already in Firestore (see `hasInvalidMemberIds` in `lib/utils.ts`) can put a
    // NaN in here - sending that to the backend 422s every time, no matter how well-timed the
    // call is. Drop the invalid entries client-side so the rest of the conversation's real
    // members still resolve, instead of failing the whole batch outright.
    const validIds = ids.filter((id) => typeof id === 'number' && !Number.isNaN(id));
    if (validIds.length === 0) return;

    try {
      const { data: allUsers } = await apiService.chatParticipants.create({
        user_ids: validIds,
      });
      // Merge by id, and hand back the very same array when nothing new came.
      //
      // This used to append unconditionally, which had two costs. The list grew
      // without bound - every ConversationItem in the sidebar calls this for its
      // own members, so the same people piled up once per conversation. And far
      // worse, each append produced a new array identity, which recomputes
      // `allMembers` in AddMemberGroupDialog, which fires the effect that reseeds
      // `setSelectedUsers(allMembers)` - throwing away whoever the user had just
      // ticked. The confirm button went back to disabled and said nothing.
      //
      // Keeping the first copy of a duplicate preserves the old behaviour:
      // lookups are `users.find(...)`, which already returned the earliest one.
      setUsers((prev) => {
        const byId = new Map(prev.map((user) => [user.id, user]));
        let added = false;
        for (const user of allUsers.data as UserType[]) {
          if (!byId.has(user.id)) {
            byId.set(user.id, user);
            added = true;
          }
        }
        return added ? Array.from(byId.values()) : prev;
      });
    } catch (error) {
      // A failed lookup for one conversation's members used to wipe out `users` entirely - since
      // this same list backs every ConversationItem in the sidebar, one bad conversation took
      // every OTHER conversation's display name/avatar down with it. Leave what's already resolved
      // alone instead; only this one lookup stays unresolved.
      console.error('Error fetching users:', error);
    }
  };

  const initializeUsers = async (memberIds: number[]) => {
    try {
      const BATCH_SIZE = 1000;
      const chunks = [];
      for (let i = 0; i < memberIds.length; i += BATCH_SIZE) {
        chunks.push(memberIds.slice(i, i + BATCH_SIZE));
      }
      for (const chunk of chunks) {
        await fetchUsers(chunk);
      }
    } catch (error) {
      console.error('Error initializing users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleConversationClick = async (conversation: ConversationType) => {
    setSelectedConversation(conversation);
    navigate(`/chat/conversation/${conversation.id}`);
  };

  const selectedUser = useMemo(() => {
    if (!id || !conversations) return null;

    const currentConversation = conversations.find((item) => item.id === id);
    const memberIds = currentConversation?.member_ids || [];

    return users?.find(
      (user) =>
        memberIds.includes(Number(user.id)) &&
        user.id !== Number(currentUser?.uid),
    );
  }, [id, conversations, users]);

  const onLogoutWhenInactive = async () => {
    try {
      await Promise.all([apiService.auth.logout(), signOut(auth)]);
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tokenExpiresAt');
      navigate(PATH.login);
    }
  };

  const handleScroll = useCallback(() => {
    const container = listContactRef.current;
    if (!container) return;
    if (
      container.scrollHeight - container.scrollTop <=
        container.clientHeight + 100 &&
      pagination?.has_more &&
      !isFetching
    ) {
      handleFetchContacts({ keyword: formMethods.watch('keyword'), limit: DEFAULT_PER_PAGE });
    }
  }, [pagination?.has_more, isFetching]);

  useEffect(() => {
    if (conversations?.length > 0) {
      setLoadingUsers(false);
      const allMemberIds = Array.from(
        new Set(conversations.filter(item => !item.is_group_chat).flatMap(conv => conv.member_ids || [])),
      );

      if (JSON.stringify(allMemberIds) !== JSON.stringify(prevMemberIds)) {
        initializeUsers(allMemberIds);
        setPrevMemberIds(allMemberIds);
      }
    }
  
    if (conversations?.length === 0) {
      setLoadingUsers(false);
    }
  }, [conversations]);

  useEffect(() => {
    setSelectedConversation(conversations.find((item) => item.id === id));
    setConversationFiltered(conversations);
    handleGetConversationFilter(String(formMethods.watch('keyword')));
  }, [conversations, id]);

  useEffect(() => {
    const shouldNeedConfirm =
      selectedConversation &&
      selectedConversation.is_group_chat &&
      selectedConversation.invitation_statuses?.[Number(currentUser?.uid)]
        ?.status === INVITE_STATUS.PENDING;
    setOpenPopup(Boolean(shouldNeedConfirm));
  }, [selectedConversation, currentUser]);

  useEffect(() => {
    const shouldOutGroup =
      selectedConversation &&
      selectedConversation.is_group_chat &&
      selectedConversation.invitation_statuses?.[Number(currentUser?.uid)]
        ?.status === INVITE_STATUS.REMOVED;
    setOpenPopupRemove(Boolean(shouldOutGroup));
  }, [selectedConversation]);

  useEffect(() => {
    if (!currentUser) return;
    const userDocRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        // A snapshot served from the local IndexedDB cache can be stale - reproduced live
        // 2026-08-26: deactivate -> reactivate -> log back in still serves the cached
        // `inactive` doc first (written while this device was logged in during the
        // deactivation), forcing a logout loop on an account that IS active again. Only a
        // server-confirmed snapshot may decide this; the confirmed one always follows right
        // behind since the listener stays open (not a one-shot read).
        if (docSnapshot.metadata.fromCache) return;
        if (docSnapshot.data().status === STATUS.INACTIVE) {
          onLogoutWhenInactive();
        }
      } else {
        console.error('User not found.');
      }
    });
    return () => unsubscribe();
  }, [currentUser?.uid]);

  useEffect(() => {
    const container = listContactRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  // Firebase Auth's session restore is async - every write below this point that reads
  // `currentUser` assumes it has already resolved (see `useFirebaseAuthUser`). Rather than
  // guard each read individually, the whole chat UI (including `CreateGroupDialog`, which is
  // mounted unconditionally as soon as `ChatAside` renders) waits behind this skeleton until
  // that restore is confirmed done - scoped to this page only, no other route is affected.
  if (!isAuthReady) {
    return (
      <div className="h-full flex items-center justify-center overflow-hidden max-h-[calc(100vh-20px)]">
        <div className="flex w-full h-full">
          <div className="w-1/3 border-r">
            <div className="px-4 pt-[8px]">
              <div className="mb-[20px] h-[40px]" />
            </div>
            <div className="mt-[18px] flex flex-col">
              {Array.from({ length: 10 }).map((_, index) => (
                <UserItemSkeleton key={index} />
              ))}
            </div>
          </div>
          <div className="w-2/3" />
        </div>
      </div>
    );
  }

  return (
    <>
      <ConfirmDialog />
      <div className="h-full flex items-center justify-center overflow-hidden max-h-[calc(100vh-20px)]">
        <div className="flex w-full h-full">
          <FormProvider {...formMethods}>
            <ChatAside
              title={t('chat.list_conversation')}
              onSubmit={handleSubmit(onSubmit)}
              initializeUsers={initializeUsers}
            >
              {formMethods.watch('keyword') === '' ? (
                <div
                  className="flex flex-col w-full overflow-y-auto max-h-[calc(100dvh-150px)]"
                  ref={listRef}
                >
                  {conversationsFilted.length > 0 &&
                    !loading &&
                    !loadingUsers &&
                    conversationsFilted.map(
                      (conversation: ConversationType) => (
                        <ConversationItem
                          key={conversation.id}
                          conversation={conversation}
                          onClick={() => handleConversationClick(conversation)}
                          isActive={
                            selectedConversation?.id === conversation.id
                          }
                          users={users}
                          isNotSeen={isNotSeenMap[conversation.id]}
                          isNeedToConfirm={isNeedConfirmMap[conversation.id]}
                          initializeUsers={initializeUsers}
                        />
                      ),
                    )}
                  {(loading || loadingUsers) && (
                    <>
                      {Array.from({ length: 10 }).map((_, index) => (
                        <UserItemSkeleton key={index} />
                      ))}
                    </>
                  )}
                  {!conversationsFilted.length && !loading && !loadingUsers && (
                    <div className="text-center mt-8 text-gray-400">
                      {t('common.form.empty')}
                    </div>
                  )}
                </div>
              ) : (
                <SearchList
                  handleConversationClick={handleConversationClick}
                  conversationsFilted={conversationsFilted}
                  loading={loading}
                  loadingUsers={loadingUsers}
                  selectedConversation={selectedConversation}
                  users={users}
                  pagination={pagination}
                  isFetching={isFetching}
                  keywordFormValue={formMethods.watch('keyword')}
                  handleFetchContacts={handleFetchContacts}
                  contacts={contacts}
                  listContactRef={listContactRef}
                />
              )}
            </ChatAside>
          </FormProvider>
          {selectedConversation && (
            <ChatBox
              key={selectedConversation?.id}
              selectedConversation={selectedConversation}
              selectedUser={selectedUser}
              messageStatus={messageStatus}
              isAccepted={isAccepted}
              isRemoved={Boolean(isRemoved)}
              users={users}
              initializeUsers={initializeUsers}
            />
          )}
          {openPopup && (
            <ConfirmJoinGroup
              selectedConversation={selectedConversation}
              users={users}
              openPopup={openPopup}
              setOpenPopup={setOpenPopup}
            />
          )}
          {openPopupRemove && (
            <ConfirmOutGroup
              selectedConversation={selectedConversation}
              users={users}
              openPopup={openPopupRemove}
              setOpenPopup={setOpenPopupRemove}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Conversation;
